// src/components/profile/DeleteAccountForm.js
import { useState } from 'react';

const DeleteAccountForm = ({ onSave, onCancel, loading }) => {
  const [action, setAction] = useState('deactivate'); // 'deactivate' or 'delete'
  const [formData, setFormData] = useState({
    password: '',
    confirmText: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (action === 'delete') {
      if (formData.confirmText !== 'DELETE') {
        newErrors.confirmText = 'Please type DELETE to confirm';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        action,
        password: formData.password
      });
    }
  };

  return (
    <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-800 rounded-lg p-6">
      <div className="flex items-start mb-6">
        <svg className="h-6 w-6 text-red-600 dark:text-red-400 mt-0.5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <h3 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            Account Deletion
          </h3>
          <p className="text-sm text-red-700 dark:text-red-400">
            This action will affect your account access. Please choose carefully.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Action Selection */}
        <div>
          <label className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-3">
            Choose an action:
          </label>
          <div className="space-y-3">
            <label className="flex items-start p-4 border-2 rounded-lg cursor-pointer hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 transition-colors">
              <input
                type="radio"
                name="action"
                value="deactivate"
                checked={action === 'deactivate'}
                onChange={(e) => setAction(e.target.value)}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <div className="font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-1">
                  Deactivate Account
                </div>
                <div className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">
                  Temporarily disable your account. You can request reactivation later. Your data will be preserved.
                </div>
              </div>
            </label>
            <label className="flex items-start p-4 border-2 border-red-300 dark:border-red-800 rounded-lg cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
              <input
                type="radio"
                name="action"
                value="delete"
                checked={action === 'delete'}
                onChange={(e) => setAction(e.target.value)}
                className="mt-1 mr-3"
              />
              <div className="flex-1">
                <div className="font-semibold text-red-800 dark:text-red-300 mb-1">
                  Permanently Delete Account
                </div>
                <div className="text-sm text-red-700 dark:text-red-400">
                  <strong>WARNING:</strong> This action cannot be undone! Your account will be deactivated immediately and permanently deleted after 2 days. All your data will be lost.
                </div>
              </div>
            </label>
          </div>
        </div>

        {/* Password */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-shayek-light-700 dark:text-shayek-dark-700 mb-2">
            Confirm Password *
          </label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-shayek-primary focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
                errors.password ? 'border-red-500' : 'border-shayek-light-300 dark:border-shayek-dark-200'
              }`}
              placeholder="Enter your password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-shayek-light-600 dark:text-shayek-dark-600"
            >
              {showPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m13.42 13.42l-3.29-3.29M3 3l13.42 13.42" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.password}</p>
          )}
        </div>

        {/* Delete Confirmation */}
        {action === 'delete' && (
          <div>
            <label htmlFor="confirmText" className="block text-sm font-medium text-red-800 dark:text-red-300 mb-2">
              Type <strong>DELETE</strong> to confirm permanent deletion *
            </label>
            <input
              id="confirmText"
              name="confirmText"
              type="text"
              value={formData.confirmText}
              onChange={handleChange}
              required
              className={`w-full px-4 py-2 border-2 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 ${
                errors.confirmText ? 'border-red-500' : 'border-red-300 dark:border-red-800'
              }`}
              placeholder="DELETE"
            />
            {errors.confirmText && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.confirmText}</p>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-shayek-light-300 dark:border-shayek-dark-200 text-shayek-light-700 dark:text-shayek-dark-700 rounded-lg hover:bg-shayek-light-50 dark:hover:bg-shayek-dark-50 focus:outline-none focus:ring-2 focus:ring-shayek-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-2 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
              action === 'delete'
                ? 'bg-red-600 dark:bg-red-700 hover:bg-red-700 dark:hover:bg-red-800 focus:ring-red-500'
                : 'bg-shayek-primary dark:bg-shayek-primary-600 hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-light focus:ring-shayek-primary'
            }`}
          >
            {loading ? 'Processing...' : action === 'delete' ? 'Permanently Delete Account' : 'Deactivate Account'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DeleteAccountForm;

